import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ConfirmDialogView from './ConfirmDialog';
import { CONFIRM_VARIANTS } from './confirmConfig';

const ConfirmDialogContext = createContext(null);

let externalConfirm = null;

const DEFAULT_STATE = {
  open: false,
  title: '',
  description: '',
  confirmLabel: 'Confirmar',
  cancelLabel: 'Cancelar',
  variant: 'danger',
};

/**
 * Provider global de confirmaciones. Montar junto a ToastProvider en App.
 *
 * Uso:
 *   const { isConfirmed } = await confirmDialog({
 *     title: '¿Eliminar lista?',
 *     description: 'Se eliminará "Mi lista".',
 *     confirmLabel: 'Eliminar',
 *     variant: 'danger',
 *   });
 */
export function ConfirmDialogProvider({ children }) {
  const [state, setState] = useState(DEFAULT_STATE);
  const resolverRef = useRef(null);

  const closeWith = useCallback((isConfirmed) => {
    resolverRef.current?.({ isConfirmed });
    resolverRef.current = null;
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const confirmDialog = useCallback((options = {}) => {
    const variant = CONFIRM_VARIANTS[options.variant] ? options.variant : 'danger';

    return new Promise((resolve) => {
      // Si había una confirmación pendiente, la cancelamos.
      resolverRef.current?.({ isConfirmed: false });
      resolverRef.current = resolve;

      setState({
        open: true,
        title: options.title || '¿Confirmar?',
        description: options.description || options.text || '',
        confirmLabel: options.confirmLabel || options.confirmButtonText || 'Confirmar',
        cancelLabel: options.cancelLabel || options.cancelButtonText || 'Cancelar',
        variant,
      });
    });
  }, []);

  useEffect(() => {
    externalConfirm = confirmDialog;
    return () => {
      externalConfirm = null;
      resolverRef.current?.({ isConfirmed: false });
      resolverRef.current = null;
    };
  }, [confirmDialog]);

  const value = useMemo(() => ({ confirmDialog }), [confirmDialog]);

  return (
    <ConfirmDialogContext.Provider value={value}>
      {children}
      <ConfirmDialogView
        open={state.open}
        title={state.title}
        description={state.description}
        confirmLabel={state.confirmLabel}
        cancelLabel={state.cancelLabel}
        variant={state.variant}
        onConfirm={() => closeWith(true)}
        onCancel={() => closeWith(false)}
      />
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirmDialog() {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) {
    throw new Error('useConfirmDialog debe usarse dentro de <ConfirmDialogProvider>');
  }
  return ctx;
}

/** API imperativa (requiere ConfirmDialogProvider montado). */
export function confirmDialog(options) {
  if (!externalConfirm) {
    console.warn('confirmDialog: ConfirmDialogProvider no está montado');
    return Promise.resolve({ isConfirmed: false });
  }
  return externalConfirm(options);
}
